// client/src/utils/formatUtils.js

/**
 * Available currencies configuration
 */
export const availableCurrencies = [
  { code: 'EUR', name: 'Euro', symbol: 'EUR' }, // 1. Euro (primo)
  { code: 'USD', name: 'US Dollar', symbol: 'USD' },// 2. Dollaro USA (secondo)  
  { code: 'GBP', name: 'British Pound', symbol: 'GBP' },// 3. Sterlina britannica (terzo)
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },// 4. Franco svizzero (quarto)
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CAD' },// 5. Dollaro canadese (quinto)
  { code: 'AUD', name: 'Australian Dollar', symbol: 'AUD' },// 6. Dollaro australiano (sesto)
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZD' }// 7. Dollaro neozelandese (settimo)
];

/**
 * Get currency symbol by currency code
 * @param {string} currencyCode - The currency code (e.g., 'EUR', 'USD')
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode) => {
  const currency = availableCurrencies.find(c => c.code === currencyCode);
  return currency ? currency.symbol : currencyCode;
};

/**
 * Get currency name by currency code
 * @param {string} currencyCode - The currency code
 * @returns {string} Currency name
 */
export const getCurrencyName = (currencyCode) => {
  const currency = availableCurrencies.find(c => c.code === currencyCode);
  return currency ? currency.name : currencyCode;
};

/**
 * Format currency with specific currency symbol
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - The currency code
 * @returns {string} Formatted currency string
 */
export const formatCurrencyWithSymbol = (amount, currencyCode = 'EUR') => {
  if (amount === null || amount === undefined) return `${getCurrencySymbol(currencyCode)}0`;
  const symbol = getCurrencySymbol(currencyCode);
  return `${symbol} ${amount.toLocaleString('en-US')}`;
};

/**
 * Format currency values with euro symbol and comma separators (backward compatibility)
 * @param {number} amount - The amount to format
 * @returns {string} Formatted currency string (e.g., €100,000)
 */
export const formatCurrency = (amount) => {
  return formatCurrencyWithSymbol(amount, 'EUR');
};

/**
 * Format interest rates with period decimal separator
 * Handles negative Euribor rates correctly and supports base rates
 * @param {number} rate - The rate to format
 * @param {string} rateType - The type of rate ('fixed' or 'floating')
 * @param {string} baseRate - The base rate for floating rates (optional, SAFE: backward compatible)
 * @returns {string} Formatted interest rate
 */
export const formatInterestRate = (rate, rateType, baseRate = null) => {
  if (rate === undefined || rate === null) return '-';
  
  const formattedRate = rate.toFixed(2) + '%';
  
  if (rateType === 'floating') {
    // NEW: If baseRate is provided, show specific base rate + spread
    if (baseRate) {
      const baseRateMap = {
      // Euribor rates
      'euribor_1w': 'EURIBOR 1W',
      'euribor_1m': 'EURIBOR 1M', 
      'euribor_3m': 'EURIBOR 3M',
      'euribor_6m': 'EURIBOR 6M',
      'euribor_12m': 'EURIBOR 12M',
      'euribor_realised': 'EURIBOR Realised',
      
      // €STR rate
      'estr': '€STR',
      'bce_dfr': 'BCE DFR',

      // SARON rates (Swiss)
      'saron_1m': 'SARON 1M',
      'saron_2m': 'SARON 2M',
      'saron_3m': 'SARON 3M',
      'saron_6m': 'SARON 6M',
      'saron_12m': 'SARON 12M',
      'saron_realised': 'SARON Realised',
      
      // USD SOFR rates
      'sofr': 'SOFR',
      'sofr_1m': 'SOFR 1M',
      'sofr_3m': 'SOFR 3M',
      'sofr_6m': 'SOFR 6M',
      'sofr_12m': 'SOFR 12M',
      'sofr_realised': 'SOFR Realised',
      'effr': 'EFFR',
      
      // GBP SONIA rates
      'sonia': 'SONIA',
      'sonia_1m': 'SONIA 1M',
      'sonia_3m': 'SONIA 3M',
      'sonia_6m': 'SONIA 6M',
      'sonia_12m': 'SONIA 12M',
      'sonia_realised': 'SONIA Realised',
      
      // CAD rates
      'corra_1m': 'CORRA 1M',
      'corra_3m': 'CORRA 3M',
      'corra_realised': 'CORRA Realised',
      'boc_overnight_rate': 'BoC Overnight Rate',
      
      // NZD rates
      'nzonia': 'NZONIA',
      'rnz_ocr': 'RNZ OCR',
      'bkbm_1m': 'BKBM 1M',
      'bkbm_3m': 'BKBM 3M',
      'bkbm_6m': 'BKBM 6M',
      'bkbm_realised': 'BKBM Realised',
      
      // AUD rates
      'aonia': 'AONIA',
      'rba_cash_rate': 'RBA Cash Rate',
      'bbsy_1m': 'BBSY 1M',
      'bbsy_2m': 'BBSY 2M',
      'bbsy_3m': 'BBSY 3M',
      'bbsy_4m': 'BBSY 4M',
      'bbsy_5m': 'BBSY 5M',
      'bbsy_6m': 'BBSY 6M',
      'bbsy_realised': 'BBSY Realised',
      
      // Central bank rates
      'ecb_rate': 'ECB Rate',
      'fed_rate': 'FED Rate',
      'boe_rate': 'BoE Rate',
      'snb_rate': 'SNB Rate',
      
      // Other rates
      'bank_rate_uk': 'UK Bank Rate',
      'prime_rate': 'Prime Rate'
      };
      
      const baseRateLabel = baseRateMap[baseRate] || baseRate.replace(/_/g, ' ').toUpperCase();
      
      // Handle negative rates properly
      if (rate < 0) {
        return `${baseRateLabel} -${Math.abs(rate).toFixed(2)}%`;
      }
      return `${baseRateLabel} +${formattedRate}`;
    } else {
      // SAFE: Existing behavior for floating rate bids without baseRate (backward compatibility)
      if (rate < 0) {
        return `Euribor -${Math.abs(rate).toFixed(2)}%`;
      }
      return `Euribor +${formattedRate}`;
    }
  } else {
    // SAFE: Existing behavior for fixed rate
    return formattedRate;
  }
};

/**
 * Helper function to format base rate display names for UI components
 * @param {string} baseRate - The base rate code
 * @param {Function} t - Translation function
 * @returns {string|null} - Formatted base rate display name or null if not provided
 */
export const formatBaseRateDisplay = (baseRate, t) => {
  if (!baseRate) return null;
  
  const baseRateMap = {
    // Euribor rates
    'euribor_1w': t('bids.euribor1Week') || 'EURIBOR 1W',
    'euribor_1m': t('bids.euribor1Month') || 'EURIBOR 1M', 
    'euribor_3m': t('bids.euribor3Months') || 'EURIBOR 3M',
    'euribor_6m': t('bids.euribor6Months') || 'EURIBOR 6M',
    'euribor_12m': t('bids.euribor12Months') || 'EURIBOR 12M',
    'estr': t('bids.estr') || '€STR',
    'bce_dfr': t('bids.bceDfr') || 'BCE DFR',

    // SARON rates (Swiss)
    'saron_3m': 'SARON 3M',
    'saron_6m': 'SARON 6M',
    'saron_12m': 'SARON 12M',
    'saron_overnight': 'SARON O/N',
    
    // USD LIBOR rates
    'libor_usd_1m': 'USD LIBOR 1M',
    'libor_usd_3m': 'USD LIBOR 3M',
    'libor_usd_6m': 'USD LIBOR 6M',
    'libor_usd_12m': 'USD LIBOR 12M',
    
    // SOFR (Secured Overnight Financing Rate)
    'sofr': 'SOFR',
    'sofr_1m': 'SOFR 1M',
    'sofr_3m': 'SOFR 3M',
    'sofr_6m': 'SOFR 6M',
    'sofr_12m': 'SOFR 12M',
    
    // GBP rates
    'sonia': 'SONIA',
    'libor_gbp_1m': 'GBP LIBOR 1M',
    'libor_gbp_3m': 'GBP LIBOR 3M',
    'libor_gbp_6m': 'GBP LIBOR 6M',
    'libor_gbp_12m': 'GBP LIBOR 12M',
    
    // Central bank rates
    'ecb_rate': t('bids.ecbRate') || 'ECB Rate',
    'fed_rate': t('bids.fedRate') || 'FED Rate',
    'boe_rate': t('bids.boeRate') || 'BoE Rate',
    'snb_rate': t('bids.snbRate') || 'SNB Rate',
    
    // Other rates
    'bank_rate_uk': t('bids.bankRateUK') || 'UK Bank Rate',
    'prime_rate': t('bids.primeRate') || 'Prime Rate'
  };
  
  // Se abbiamo una traduzione specifica, usala
  if (baseRateMap[baseRate]) {
    return baseRateMap[baseRate];
  }
  
  // Fallback: formatta il codice grezzo in modo leggibile
  return baseRate
    .replace(/_/g, ' ')
    .toUpperCase();
};

/**
 * Formats a number as currency input with thousands separators (US format with commas)
 * @param {number|string} value - The value to format
 * @returns {string} - Formatted currency string with commas as thousands separators
 */
export const formatCurrencyInput = (value) => {
  const str = value.toString();

  // Strip commas first — they are always thousands separators from our own previous formatting.
  // Then keep only digits and dots (dot = decimal separator).
  const cleaned = str.replace(/,/g, '').replace(/[^\d.]/g, '');
  if (!cleaned) return '';

  // Split on the last dot (decimal separator)
  const lastDot = cleaned.lastIndexOf('.');

  let integerPart, decimalPart;

  if (lastDot >= 0) {
    // Has decimal part
    integerPart = cleaned.substring(0, lastDot).replace(/\./g, '');
    decimalPart = cleaned.substring(lastDot + 1);
  } else {
    // No decimal separator, just digits
    integerPart = cleaned;
    decimalPart = null;
  }

  if (!integerPart && !decimalPart) return '';

  // Format integer part with thousands separators (US format)
  const number = parseInt(integerPart || '0');
  const formattedInteger = number.toLocaleString('en-US');

  // Append decimal part if present (limit to 2 digits)
  if (decimalPart !== null) {
    return formattedInteger + '.' + decimalPart.substring(0, 2);
  }

  return formattedInteger;
};

/**
 * Parses a formatted currency string back to a number
 * @param {string} formattedValue - The formatted currency string
 * @returns {number} - The numeric value
 */
export const parseCurrencyInput = (formattedValue) => {
  if (!formattedValue) return 0;
  const str = formattedValue.toString();

  // Strip commas (thousands separators from our formatting), keep only digits and dot
  const cleaned = str.replace(/,/g, '').replace(/[^\d.]/g, '');

  return parseFloat(cleaned) || 0;
};

/**
 * Handles currency input change events with automatic formatting
 * @param {Event} e - The input change event
 * @param {Function} setFormData - Function to update form state
 * @param {Object} formData - Current form data
 * @param {string} fieldName - Name of the field being updated
 */
export const handleCurrencyInputChange = (e, setFormData, formData, fieldName) => {
  const rawValue = e.target.value;

  // Allow the user to type a trailing decimal separator (dot or comma) without formatting it away
  // This prevents the cursor from jumping while the user is typing decimals
  if (rawValue.endsWith('.') || rawValue.endsWith(',')) {
    const formatted = formatCurrencyInput(rawValue.slice(0, -1));
    setFormData({
      ...formData,
      [fieldName]: formatted ? formatted + '.' : ''
    });
    return;
  }

  const formatted = formatCurrencyInput(rawValue);
  setFormData({
    ...formData,
    [fieldName]: formatted
  });
};

/**
 * Format duration values handling overnight, demand, and months
 * @param {string|number} duration - The duration value
 * @param {Function} t - Translation function
 * @returns {string} Formatted duration string
 */
export const formatDuration = (duration, t, customStartDate, customEndDate) => {
  if (!duration) return 'N/A';

  // Handle custom duration
  if (duration === 'custom') {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate).toLocaleDateString();
      const end = new Date(customEndDate).toLocaleDateString();
      return `${t('requests.custom')} (${start} - ${end})`;
    }
    return t('requests.custom');
  }

  // Handle overnight
  if (duration === 'overnight') {
    return t('requests.overnight');
  }

  // Handle demand (se presente)
  if (duration === 'demand') {
    return t('requests.demand');
  }

  // Handle days format (7d, 14d, 21d)
  if (typeof duration === 'string' && duration.endsWith('d')) {
    const days = parseInt(duration);
    return `${days} ${t('common.days')}`;
  }

  // Handle months format (1, 2, 3, 6, 12)
  const months = parseInt(duration);
  if (isNaN(months)) return duration;

  return months === 1
    ? `1 ${t('common.month')}`
    : `${months} ${t('common.months')}`;
};

/**
 * Calculate commission for non-paying companies (isPaying = false)
 * Client-side calculation for preview/display purposes
 * @param {Number} amount - The allocated amount in EUR
 * @param {String} duration - Duration string ('overnight', '1', '2', '3', '6', '12', '18', '24')
 * @param {Boolean} isPaying - Whether the company is a paying customer
 * @returns {Number|null} - Commission amount or null if not applicable
 */
export const calculateCommission = (amount, duration, isPaying) => {
  // Only calculate commission for non-paying companies
  if (isPaying !== false) return null;

  // For overnight (current account), fixed commission of 100 EUR
  if (duration === 'overnight') {
    return 100;
  }

  // Convert duration to months
  const months = parseInt(duration);
  if (isNaN(months) || months <= 0) return null;

  // Progressive tiered calculation
  let commission = 0;

  if (amount <= 1000000) {
    // First tier: 0.25% annual rate
    commission = amount * (months / 12) * 0.0025;
  } else if (amount <= 5000000) {
    // First 1M at 0.25%
    commission = 1000000 * (months / 12) * 0.0025;
    // Rest up to 5M at 0.10%
    commission += (amount - 1000000) * (months / 12) * 0.0010;
  } else {
    // First 1M at 0.25%
    commission = 1000000 * (months / 12) * 0.0025;
    // Next 4M at 0.10%
    commission += 4000000 * (months / 12) * 0.0010;
    // Rest above 5M at 0.05%
    commission += (amount - 5000000) * (months / 12) * 0.0005;
  }

  // Round to 2 decimal places
  return Math.round(commission * 100) / 100;
};

/**
 * Calculate estimated earnings for display purposes
 * @param {Number} amount - The allocated amount
 * @param {String} duration - Duration string
 * @param {Number} interestRate - Interest rate as percentage (e.g., 2.5 for 2.5%)
 * @returns {Number|null} - Estimated earnings or null for overnight
 */
export const calculateEstimatedEarnings = (amount, duration, interestRate) => {
  // Don't show earnings for overnight accounts
  if (duration === 'overnight') return null;

  // Convert duration to months
  const months = parseInt(duration);
  if (isNaN(months) || months <= 0) return null;

  // Calculate: Amount × (Duration/12) × (InterestRate/100)
  const earnings = amount * (months / 12) * (interestRate / 100);

  // Round to 2 decimal places
  return Math.round(earnings * 100) / 100;
};

/**
 * Get commission rate display string for table column
 * Based on the REQUEST amount, not the bid amount
 * @param {Number} requestAmount - The total request amount
 * @param {String} duration - Duration string
 * @returns {String} - Rate display string (e.g., "0.25%", "0.10%", "0.05%", "100€")
 */
export const getCommissionRateDisplay = (requestAmount, duration) => {
  if (duration === 'overnight') {
    return '100€';
  }

  if (requestAmount < 1000000) {
    return '0.25%';
  } else if (requestAmount < 5000000) {
    return '0.10%';
  } else {
    return '0.05%';
  }
};